import { graphql, useStaticQuery } from "gatsby"
import React, { createContext, useReducer } from "react"
import { useTranslation } from "react-i18next"

import { ApolloProvider } from "@apollo/client"

import { ApolloClientRuntime } from "../components/atoms/ApolloClientRuntime/ApolloClientRuntime"

export const SiteContext = createContext()

const Provider = (props) => {
  const { t } = useTranslation()

  const { allPractices, allServices } =
    useStaticQuery(
      graphql`
        query allPractices {
          allPractices: allWpPractice(sort: { slug: ASC }) {
            nodes {
              id
              uri
              name
              practicesMeta {
                area
              }
            }
          }
          allServices: allWpService(sort: { slug: ASC }) {
            nodes {
              id
              uri
              title
              parentId
              practiceAreas {
                nodes {
                  slug
                }
              }
            }
          }
        }
      `
    ) || {}

  const practicesArray = allPractices.nodes.length > 0 ? allPractices.nodes : []

  const practicesGrouped = [
    {
      heading: t("Transactions"),
      headingReferences: t("Transactions References"),
      items: practicesArray.filter(
        (item) =>
          item.practicesMeta?.area === "transactions" &&
          item.name !== "Pro Bono"
      ),
    },
    {
      heading: t("Advisory"),
      headingReferences: t("Advisory References"),
      items: practicesArray.filter(
        (item) =>
          item.practicesMeta?.area === "advisory" &&
          item.name !== "Sanctions & Export Controls" &&
          item.name !== "Pro Bono"
      ),
    },
    {
      heading: t("Dispute Resolution"),
      headingReferences: t("Dispute Resolution References"),
      items: practicesArray.filter(
        (item) =>
          item.practicesMeta?.area === "dispute-resolution" &&
          item.name !== "Pro Bono"
      ),
    },
  ]

  // Services

  const servicesArray =
    allServices.nodes.length > 0
      ? allServices.nodes.filter((item) => !item.parentId)
      : []

  const servicesGrouped = [
    {
      heading: t("Transactions"),
      items: servicesArray.filter((item) => {
        const practiceAreas = item?.practiceAreas?.nodes.map(
          (area) => area.slug
        )
        return practiceAreas.includes("transactions")
      }),
    },
    {
      heading: t("Advisory"),
      items: servicesArray.filter((item) => {
        const practiceAreas = item?.practiceAreas?.nodes.map(
          (area) => area.slug
        )
        return practiceAreas.includes("advisory")
      }),
    },
    {
      heading: t("Dispute Resolution"),
      items: servicesArray.filter((item) => {
        const practiceAreas = item?.practiceAreas?.nodes.map(
          (area) => area.slug
        )
        return practiceAreas.includes("dispute-resolution")
      }),
    },
  ]

  // People Grid

  const initialFilterState = {
    offices: [],
    positions: [],
    serviceAreas: [],
    languages: [],
    keyword: "",
  }

  const peopleReducer = (state, { type, payload, clear }) => {
    let newState = { ...state } || {}

    switch (type) {
      case "reset":
        newState = initialFilterState
        break
      case "keyword":
        newState.keyword = payload
        break
      case "offices":
        if (clear) {
          newState.offices = []
        } else {
          newState.offices = state.offices.includes(payload)
            ? state.offices.filter((item) => item !== payload)
            : [payload, ...state.offices]
        }
        break
      case "positions":
        if (clear) {
          newState.positions = []
        } else {
          newState.positions = state.positions.includes(payload)
            ? state.positions.filter((item) => item !== payload)
            : [payload, ...state.positions]
        }
        break
      case "serviceAreas":
        if (clear) {
          newState.serviceAreas = []
        } else {
          newState.serviceAreas = payload.every((item) =>
            state.serviceAreas.includes(item)
          )
            ? state.serviceAreas.filter((item) => !payload.includes(item))
            : [...state.serviceAreas].concat(...payload)
        }
        break
      case "languages":
        if (clear) {
          newState.languages = []
        } else {
          newState.languages = state.languages.includes(payload)
            ? state.languages.filter((item) => item !== payload)
            : [payload, ...state.languages]
        }

        break
      default:
        throw new Error(`Unhandled action type: ${type}`)
    }

    return newState
  }

  const [state, dispatch] = useReducer(peopleReducer, initialFilterState)

  return (
    <SiteContext.Provider
      value={{
        practicesGrouped,
        servicesGrouped,
        practices: practicesArray,
        services: servicesArray,
        peopleGridState: state,
        peopleGridDispatch: dispatch,
      }}
    >
      {props.children}
    </SiteContext.Provider>
  )
}
// eslint-disable-next-line import/no-anonymous-default-export
export default ({ element }) => (
  <ApolloProvider client={ApolloClientRuntime}>
    <Provider>{element}</Provider>
  </ApolloProvider>
)
